import { Component, inject, signal, computed, ChangeDetectionStrategy, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { LicenseDataService } from '../../core/services/license-data.service';
import { InstitutionalLicense } from '../../shared/models/license.models';

/**
 * Institutional Settings Component
 *
 * Manages institutional email addresses for license groups
 * Allows users to add/remove institutional emails and verify them
 */
@Component({
  selector: 'app-institutional-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDividerModule,
    MatTooltipModule,
    MatTableModule
  ],
  templateUrl: './institutional-settings.component.html',
  styleUrls: ['./institutional-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InstitutionalSettingsComponent {
  private fb = inject(FormBuilder);
  private snackBar = inject(MatSnackBar);
  private licenseDataService = inject(LicenseDataService);

  @Output() listChanged = new EventEmitter<void>();

  // Signals for component state
  isLoading = signal(false);
  emailCount = signal(0);
  pendingEmail = signal<string | null>(null);
  
  // Registered emails list
  registeredEmailsList = signal<Array<{
    emailAddress: string;
    isVerified: boolean;
    isActive: boolean;
    domain: string;
  }>>([]);

  // Institutions data
  institutions = signal<InstitutionalLicense[]>([]);
  displayedColumns: string[] = ['name', 'description', 'licenseTier'];

  // Input for institutions (passed from parent PlansComponent)
  @Input() set institutionalLicenses(value: InstitutionalLicense[]) {
    // Map the complex object to a flatter structure for the table
    // The object has groupInfo { name, description } etc.
    const mappedInstitutions = value.map(inst => {
      // Use the existing properties or map from groupInfo/licenseData if they are missing
      return {
        ...inst,
        name: inst.name || inst.groupInfo?.name || 'Unknown Institution',
        description: inst.description || inst.groupInfo?.description || inst.licenseData?.name || '-',
        // Extract license tier nicely
        licenseTier: inst.licenseData?.tier ? 
          (inst.licenseData.tier.charAt(0).toUpperCase() + inst.licenseData.tier.slice(1)) : 
          'Unknown'
      };
    });
    
    this.institutions.set(mappedInstitutions);
    console.log('🏛️ [INSTITUTIONAL] Institutions mapped and updated:', mappedInstitutions);
  }

  // Input for registered emails
  @Input() set registeredEmails(value: any[]) {
    console.log('📧 [INSTITUTIONAL] Received registeredEmails input:', value);
    this.registeredEmailsList.set(value || []);
  }

  // Form for adding new institutional email
  addEmailForm: FormGroup;

  constructor() {
    console.log('🏛️ [INSTITUTIONAL] Institutional settings component initialized');

    // Initialize add email form with separate username and domain fields
    this.addEmailForm = this.fb.group({
      username: ['', [Validators.required]],
      domain: ['', [Validators.required]]
    });
  }

  /**
   * Add a new institutional email
   */
  async addInstitutionalEmail(): Promise<void> {
    if (!this.addEmailForm.valid) {
      this.snackBar.open('Please fill in both username and domain', 'Close', { duration: 3000 });
      return;
    }

    const username = this.addEmailForm.get('username')?.value;
    const domain = this.addEmailForm.get('domain')?.value;
    const emailAddress = `${username}@${domain}`;

    console.log('📧 [INSTITUTIONAL] Adding email:', emailAddress);

    if (!this.isValidInstitutionalEmail(emailAddress)) {
      this.snackBar.open('Please use an institutional email domain', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);

    try {
      console.log('📧 [INSTITUTIONAL] Calling licenseDataService.addInstitutionalEmail...');
      const result = await this.licenseDataService.addInstitutionalEmail(emailAddress);
      console.log('📧 [INSTITUTIONAL] Cloud function result:', result);

      this.snackBar.open('Institutional email added successfully!', 'Close', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });

      this.pendingEmail.set(emailAddress);
      this.addEmailForm.reset();
      
      console.log('📧 [INSTITUTIONAL] Emitting listChanged event to trigger reload...');
      this.listChanged.emit();

    } catch (error: any) {
      console.error('❌ [INSTITUTIONAL] Failed to add email:', error);
      console.error('❌ [INSTITUTIONAL] Error details:', error.message, error.code, error.details);

      let errorMessage = 'Failed to add institutional email';
      if (error.message?.includes('INVALID_FORMAT')) {
        errorMessage = 'Please enter a valid email address';
      } else if (error.message?.includes('QUOTA_EXCEEDED')) {
        errorMessage = 'Maximum number of emails reached';
      } else if (error.message?.includes('ALREADY_EXISTS')) {
        errorMessage = 'This email is already associated with an account';
      }

      this.snackBar.open(errorMessage, 'Close', { duration: 3000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Remove an institutional email
   */
  async removeInstitutionalEmail(emailAddress: string): Promise<void> {
    if (!confirm(`Are you sure you want to remove ${emailAddress} from your account?`)) {
      return;
    }

    this.isLoading.set(true);

    try {
      await this.licenseDataService.removeInstitutionalEmail(emailAddress);
      console.log('🗑️ [INSTITUTIONAL] Removing email:', emailAddress);

      this.snackBar.open('Institutional email removed successfully!', 'Close', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
      
      this.listChanged.emit();

    } catch (error: any) {
      console.error('❌ [INSTITUTIONAL] Failed to remove email:', error.message || error);

      let errorMessage = 'Failed to remove institutional email';
      if (error.message?.includes('NOT_FOUND')) {
        errorMessage = 'Email address not found on your account';
      }

      this.snackBar.open(errorMessage, 'Close', { duration: 3000 });
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Check if the entered email is an institutional email (has a domain)
   */
  isValidInstitutionalEmail(email: string): boolean {
    if (!email) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    // Check if it's not a common consumer email domain
    const consumerDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();

    return !consumerDomains.includes(domain);
  }

  /**
   * Send verification email
   */
  sendVerificationEmail(emailToVerify?: string): void {
    const email = emailToVerify || this.pendingEmail();
    if (!email) {
      return;
    }

    this.isLoading.set(true);

    // Simulate async operation
    setTimeout(() => {
      console.log('📧 [INSTITUTIONAL] Verification email sent to:', email);

      this.snackBar.open(`Verification email sent to ${email}`, 'Close', {
        duration: 5000,
        panelClass: ['success-snackbar']
      });

      this.isLoading.set(false);
    }, 1000);
  }
}
